
/**
 * Advanced Error Logging System
 * 
 * Provides standardized error logging with contextual information
 * and integrates with the application's global error tracking system
 */

import { v4 as uuidv4 } from 'uuid';

// Error severity levels
export enum ErrorSeverity {
  DEBUG = 'debug',    // Development-only messages
  INFO = 'info',      // Informational messages
  WARNING = 'warning', // Warnings that don't break functionality
  ERROR = 'error',    // Standard errors that impact functionality
  CRITICAL = 'critical', // Severe errors that break core functionality
}

// Log entry interface
export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  message: string;
  details?: any;
  componentName?: string;
  severity: ErrorSeverity;
  stackTrace?: string;
  userInfo?: {
    authenticated: boolean;
    route?: string;
    action?: string;
  };
  metadata?: Record<string, any>;
}

// In-memory log storage (last 50 entries in production, 100 in development)
const errorLog: ErrorLogEntry[] = [];
const MAX_LOG_SIZE = process.env.NODE_ENV === 'production' ? 50 : 100;

// Log subscribers for real-time monitoring - only used in development
const subscribers: Array<(entry: ErrorLogEntry) => void> = [];

/**
 * Log an error or message to the centralized logging system
 * Optimized to reduce overhead in production
 */
export const logError = (
  message: string | Error,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  componentName?: string,
  details?: any
): ErrorLogEntry => {
  // Skip DEBUG level logs in production
  if (process.env.NODE_ENV === 'production' && severity === ErrorSeverity.DEBUG) {
    return {
      id: 'debug-skipped',
      timestamp: new Date().toISOString(),
      message: typeof message === 'string' ? message : message.message,
      severity: ErrorSeverity.DEBUG
    };
  }
  
  try {
    // Normalize error messages
    const errorMessage = message instanceof Error ? message.message : message;
    const stackTrace = message instanceof Error ? message.stack : undefined;
    
    // Create log entry
    const entry: ErrorLogEntry = {
      id: uuidv4(),
      timestamp: new Date().toISOString(),
      message: errorMessage,
      details,
      componentName,
      severity,
      stackTrace,
      userInfo: {
        authenticated: false,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      },
      // Only include non-essential metadata in development
      metadata: process.env.NODE_ENV !== 'production' ? {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        platform: typeof navigator !== 'undefined' ? navigator.platform : undefined,
      } : undefined
    };
    
    // Add to in-memory log with size limit
    errorLog.unshift(entry);
    
    // Trim log if too large
    if (errorLog.length > MAX_LOG_SIZE) {
      errorLog.pop();
    }
    
    // Console output based on severity - more minimal in production
    if (process.env.NODE_ENV === 'production') {
      switch (severity) {
        case ErrorSeverity.INFO:
          // Skip info logs in production for less console noise
          break;
        case ErrorSeverity.WARNING:
          console.warn(`⚠️ [${componentName || 'App'}]`, errorMessage);
          break;
        case ErrorSeverity.ERROR:
        case ErrorSeverity.CRITICAL:
          console.error(`❌ [${componentName || 'App'}]`, errorMessage);
          break;
      }
    } else {
      // More verbose in development
      switch (severity) {
        case ErrorSeverity.DEBUG:
          console.debug(`🔍 [${componentName || 'App'}]`, errorMessage, details);
          break;
        case ErrorSeverity.INFO:
          console.info(`ℹ️ [${componentName || 'App'}]`, errorMessage, details);
          break;
        case ErrorSeverity.WARNING:
          console.warn(`⚠️ [${componentName || 'App'}]`, errorMessage, details);
          break;
        case ErrorSeverity.ERROR:
          console.error(`❌ [${componentName || 'App'}]`, errorMessage, details);
          break;
        case ErrorSeverity.CRITICAL:
          console.error(`🔥 CRITICAL [${componentName || 'App'}]`, errorMessage, details);
          break;
      }
    }
    
    // Store in localStorage for persistence ONLY for critical errors in production
    // Store all ERROR and CRITICAL in development
    if ((process.env.NODE_ENV === 'production' && severity === ErrorSeverity.CRITICAL) ||
        (process.env.NODE_ENV !== 'production' && 
         (severity === ErrorSeverity.ERROR || severity === ErrorSeverity.CRITICAL))) {
      try {
        const storageKey = `error_log_${entry.id}`;
        localStorage.setItem(storageKey, JSON.stringify(entry));
        
        // Clean up old logs in localStorage - reduced in production
        const maxStoredLogs = process.env.NODE_ENV === 'production' ? 10 : 20;
        const errorKeys = Object.keys(localStorage)
          .filter(key => key.startsWith('error_log_'))
          .sort();
          
        if (errorKeys.length > maxStoredLogs) {
          errorKeys.slice(0, errorKeys.length - maxStoredLogs).forEach(key => {
            try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
          });
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    // Notify subscribers - only in development
    if (process.env.NODE_ENV !== 'production') {
      subscribers.forEach(subscriber => {
        try {
          subscriber(entry);
        } catch (e) {
          // Ignore subscriber errors
        }
      });
    }
    
    return entry;
  } catch (e) {
    // Failsafe for errors in the error logger
    console.error('Error in error logger:', e);
    return {
      id: uuidv4(),
      timestamp: new Date().toISOString(),
      message: 'Error in error logger',
      severity: ErrorSeverity.ERROR
    };
  }
};

/**
 * Get recent error logs
 */
export const getErrorLogs = (): ErrorLogEntry[] => {
  return [...errorLog];
};

/**
 * Subscribe to real-time error logs - only for development use
 * @returns Unsubscribe function
 */
export const subscribeToErrors = (callback: (entry: ErrorLogEntry) => void): () => void => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('Error subscriptions should not be used in production');
    return () => {};
  }
  
  subscribers.push(callback);
  return () => {
    const index = subscribers.indexOf(callback);
    if (index !== -1) {
      subscribers.splice(index, 1);
    }
  };
};

/**
 * Clear all error logs
 */
export const clearErrorLogs = (): void => {
  errorLog.length = 0;
};

export default {
  logError,
  getErrorLogs,
  subscribeToErrors,
  clearErrorLogs,
  ErrorSeverity
};
